import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { FETCH_LOYALTY_POINTS_BURN_BATCH_CALCULATION } from 'actions/apiActionConstants'
import createBatchedPromise from 'api/createBatchedPromise'
import { requestLoyaltyPointsBurnCalculationBatch, type BurnBatchRequest, type BurnBatchCalculationOptions } from 'api/loyalty/requestLoyaltyPointsBurnCalculationBatch'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'

interface BundledSingleArg {
  request: BurnBatchRequest
  key: string
}

/**
 * Bundles individual burn calculation requests into a single batch API call.
 * Requests are accumulated over 32ms and sent together.
 */
const bundleLoyaltyPointsBurnCalculationForBatchRequest = createBatchedPromise<
  Record<string, App.LoyaltyPointsBurnBatchItem>,
  App.LoyaltyPointsBurnBatchItem,
  BundledSingleArg,
  BundledSingleArg,
  string,
  BurnBatchCalculationOptions
>({
  waitTime: 32,
  argsReducerFn: (accumulated, singleArg) => ({
    ...accumulated,
    [singleArg.key]: singleArg,
  }),
  batchRequestFn: async(accumulated, options) => {
    if (!options) {
      throw new Error('BurnBatchCalculationOptions are required for batch burn calculation')
    }
    const requests = Object.values(accumulated).map(({ request }) => request)
    return requestLoyaltyPointsBurnCalculationBatch(requests, options)
  },
  singleValueFn: (singleArg, batchResponse) => {
    const result = batchResponse[singleArg.key]
    if (!result) {
      throw new Error(`Loyalty burn calculation failed for key: ${singleArg.key}`)
    }
    return result
  },
})

export default function submitLoyaltyPointsBurnCalculationRequest(
  calculationRequest: BurnBatchRequest,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const isCurrentRegionUserLoyaltyRegion = getIsCurrentRegionUserLoyaltyRegion(state)

    if (
      !getIsLoyaltyEnabled(state) ||
      (hasLoyaltyAccount && !isCurrentRegionUserLoyaltyRegion)
    ) return

    // Member id is only used for cache keys; burn batch pricing supports guests (null account).
    const customerId = state.auth.account.memberId ?? ''

    const currency = state.geo.currentCurrency
    const burnBatchKey = buildBurnKey(calculationRequest, customerId)

    const existing = state.loyalty.pointsBurnBatchCalculations?.[burnBatchKey]
    if (existing?.data || existing?.fetching || existing?.error) return

    dispatch({
      type: API_CALL,
      api: FETCH_LOYALTY_POINTS_BURN_BATCH_CALCULATION,
      burnBatchKey,
      meta: {
        [SHOULD_AUTOBATCH]: true,
      },
      request: () => bundleLoyaltyPointsBurnCalculationForBatchRequest(
        { request: calculationRequest, key: burnBatchKey },
        { customerId, currency },
      ),
    })
  }
}
